import api from "./api";

// Facility attendance log (system-user / RBAC — gated by "view_attendance" on
// the backend), plus the live scan-and-record endpoint below (gated by
// "scan_member_qr"). The "approve_entry" / "deny_entry" permissions are still
// seeded with no backend route at all yet.

// GET /api/external-member-attendances — optional { member_id, booking_id,
// date } filters (no free-text search on this endpoint). Same underlying
// data/controller as the older GET /api/attendances (AttendanceController::
// index) — this is the current name for it, scoped to SFMIS members who
// booked a facility. Returns a plain array of rows: {id, member_id,
// member_code, member_title, member_initials,
// member_name_denoted_by_initials, member_lastname, member_email,
// booking_id, booking_date, total_slots, total_fee, scanned_code,
// scanned_at, created_at, updated_at}.
export const listExternalMemberAttendances = (params = {}) =>
  api.get("/api/external-member-attendances", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/internal-student-attendances — optional { registration_no,
// faculty, year, date } filters (no free-text search). Covers university
// students who scan in without an SFMIS membership. Returns a plain array of
// rows: {id, registration_no, title, name_with_initial, faculty, year,
// scanned_at, scanned_by, created_at, updated_at}.
export const listInternalStudentAttendances = (params = {}) =>
  api.get("/api/internal-student-attendances", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/attendance-without-booking/external-members — optional
// { member_id, status, date } filters (no free-text search). The walk-in
// counterpart to listExternalMemberAttendances above: SFMIS members who
// scanned in via the "Scan Attendance" tab (POST /api/attendance-without-booking/scan)
// without a facility booking, i.e. rows in attendance_without_booking whose
// person type is "External Member" — same table/shape family as
// listInternalStudentAttendances, just filtered to the other person type.
// Returns a plain array of rows: {id, member_id, member_code, attendance_person_type,
// status, scanned_by_name, member_email, primary_phone_number, member_status_id,
// person_identifier, scanned_at, created_at, updated_at}. member_email/
// primary_phone_number/member_status_id are null when person_identifier isn't
// a resolvable member_registration id.
export const listExternalMembersWithoutBooking = (params = {}) =>
  api.get("/api/attendance-without-booking/external-members", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/members/{memberCode}/attendances — optional { date, status }
// filters, gated by "view_attendance", driven from the member detail page
// (MembersManagement.jsx). `memberCode` must be the public "MEM000502" form —
// like the sibling .../bookings route, this one is regex-constrained to
// MEM[0-9]+ (routes/api.php), a raw numeric id 404s before ever reaching
// AttendanceController::forMember. Only covers WALK-IN attendance
// (attendance_without_booking, "External Member" rows) — forMember() never
// merges in booking-linked attendance (external_member_attendance, what
// listExternalMemberAttendances above covers), so this is a partial history
// for a member who scanned in against a facility booking rather than through
// the walk-in scanner. Response wraps the array under an `attendances` key
// alongside member identity; this unwraps straight to that array. Rows:
// {id, scanned_code, member_id, member_code, member_name, title,
// name_with_initial, scanned_at, scanned_out_at, duration_time, status,
// attendance_person_type, scanned_by_name, created_at, updated_at}.
export const getMemberAttendances = (memberCode, params = {}) =>
  api.get(`/api/members/${memberCode}/attendances`, { params }).then((r) => r.data?.attendances || []);

// POST /api/attendance-without-booking/scan — resolves and records a single
// scanned code against either an SFMIS member (numeric code = member_registration
// id) or a university student (alphanumeric code, verified against an external
// SIMS API on the backend), then always records a row in
// attendance_without_booking regardless of the outcome.
//
// The backend always returns a fully-shaped body — even rejections (invalid
// code, member/student not found, membership/active-status invalid, SIMS
// service unavailable) come back as a 4xx/5xx *with* the same
// {success, message, type, status, attendance_id, ...} JSON as a 2xx success.
// So this resolves on both 2xx and "handled" error responses instead of only
// on success — only a response-less failure (network/timeout) rejects.
const resolveScan = (request) =>
  request
    .then((r) => r.data)
    .catch((err) => {
      if (err?.response?.data) return err.response.data;
      throw err;
    });

export const scanAttendanceCode = (scannedCode) =>
  resolveScan(api.post("/api/attendance-without-booking/scan", { scanned_code: scannedCode }));

// POST /api/attendance/scan — same request shape and same "resolve on handled
// rejections" contract as scanAttendanceCode above, but an external member's
// check-in is only recorded (in external_member_attendance) against a
// confirmed booking for the current time; a second scan while that visit is
// open checks them out. Internal-student codes are routed server-side to the
// same walk-in flow as the without-booking endpoint. External-member
// responses carry `mode: "booking"` but no `type`/`status` fields, plus
// booking_id/facility_name/slot_code/start_time/end_time on check-in and
// `duration_time` ("HH:MM:SS") rather than `duration` on check-out.
export const scanBookingAttendanceCode = (scannedCode) =>
  resolveScan(api.post("/api/attendance/scan", { scanned_code: scannedCode }));
